import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Input,
  Row,
} from "reactstrap";
import { toast } from "react-toastify";
import { useEnquirySiteVisitOverview } from "./EnquirySiteVisitOverviewContext";
import { EnquirySiteVisitStatus } from "../constants/enquiry-site-visit.constants";
import type { SiteVisitChecklistItem } from "../types/enquiry-site-visit.types";
import { updateSiteVisitChecklist } from "../services/EnquirySiteVisitService";

const ChecklistTab = () => {
  const { enquirySiteVisit, refetch } = useEnquirySiteVisitOverview();
  const [items, setItems] = useState<SiteVisitChecklistItem[]>([]);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [expandedFindings, setExpandedFindings] = useState<Set<number>>(
    new Set(),
  );
  const newItemInputRef = useRef<HTMLInputElement | null>(null);

  const status = enquirySiteVisit?.status as string | undefined;
  const isTerminal =
    status === EnquirySiteVisitStatus.COMPLETED ||
    status === EnquirySiteVisitStatus.CANCELLED;
  const isProgressPhase = status === EnquirySiteVisitStatus.IN_PROGRESS;
  const canManageStructure = !isTerminal && !isProgressPhase;
  const canFillFindings = !isTerminal;

  useEffect(() => {
    const raw = enquirySiteVisit?.checklist;
    const sorted =
      Array.isArray(raw) && raw.length > 0
        ? [...raw].sort((a, b) => a.sortOrder - b.sortOrder)
        : [];
    setItems(sorted);
    setExpandedFindings(
      new Set(
        sorted.reduce<number[]>((acc, item, idx) => {
          if (item.finding) acc.push(idx);
          return acc;
        }, []),
      ),
    );
    setDirty(false);
  }, [enquirySiteVisit?.checklist]);

  const save = useCallback(
    async (currentItems: SiteVisitChecklistItem[]) => {
      if (!enquirySiteVisit?.id) return;
      setSaving(true);
      try {
        const normalized = currentItems.map((item, idx) => ({
          ...item,
          sortOrder: idx,
          label: item.label.trim(),
          finding: item.finding?.trim() || null,
        }));
        const res = await updateSiteVisitChecklist(
          enquirySiteVisit.id,
          normalized,
        );
        if (res.success) {
          setDirty(false);
          await refetch();
        } else {
          toast.error(res.message ?? "Failed to save checklist.");
        }
      } catch {
        toast.error("Failed to save checklist.");
      } finally {
        setSaving(false);
      }
    },
    [enquirySiteVisit?.id, refetch],
  );

  const handleAddItem = () => {
    const nextOrder =
      items.length > 0 ? Math.max(...items.map((i) => i.sortOrder)) + 1 : 0;
    setItems((prev) => [
      ...prev,
      { label: "", finding: null, isCompleted: false, sortOrder: nextOrder },
    ]);
    setDirty(true);
    setTimeout(() => newItemInputRef.current?.focus(), 30);
  };

  const handleRemoveItem = (idx: number) => {
    setItems((prev) => prev.filter((_, i) => i !== idx));
    setExpandedFindings((prev) => {
      const next = new Set<number>();
      prev.forEach((n) => {
        if (n < idx) next.add(n);
        else if (n > idx) next.add(n - 1);
      });
      return next;
    });
    setDirty(true);
  };

  const handleItemChange = (
    idx: number,
    updates: Partial<SiteVisitChecklistItem>,
  ) => {
    setItems((prev) =>
      prev.map((item, i) => (i === idx ? { ...item, ...updates } : item)),
    );
    setDirty(true);
  };

  const toggleFinding = (idx: number) => {
    setExpandedFindings((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  return (
    <Row className="g-3 mt-0">
      <Col lg={12}>
        <Card>
          <CardHeader className="d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div>
              <h6 className="mb-0 fw-semibold">Inspection Checklist</h6>
              {saving ? (
                <p className="mb-0 text-muted small mt-1">
                  <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                  Saving…
                </p>
              ) : null}
            </div>
            {!isTerminal ? (
              <div className="d-flex gap-2">
                {canManageStructure ? (
                  <Button
                    type="button"
                    color="light"
                    size="sm"
                    className="border"
                    onClick={handleAddItem}
                    disabled={saving}
                  >
                    <i className="ri-add-line align-bottom me-1" />
                    Add item
                  </Button>
                ) : null}
                {dirty ? (
                  <Button
                    type="button"
                    color="primary"
                    size="sm"
                    onClick={() => void save(items)}
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-1"
                          role="status"
                          aria-hidden="true"
                        />
                        Saving…
                      </>
                    ) : (
                      "Save changes"
                    )}
                  </Button>
                ) : null}
              </div>
            ) : null}
          </CardHeader>

          <CardBody>
            {items.length === 0 ? (
              <div className="text-center py-5">
                <i className="ri-checkbox-multiple-line display-5 text-muted opacity-50" />
                <p className="text-muted mt-3 mb-1">
                  {isTerminal
                    ? "No checklist items were added to this visit."
                    : canManageStructure
                      ? "No items yet."
                      : "No checklist items were set up for this visit."}
                </p>
                {canManageStructure ? (
                  <Button
                    type="button"
                    color="primary"
                    size="sm"
                    className="mt-2"
                    onClick={handleAddItem}
                  >
                    <i className="ri-add-line me-1" />
                    Add first item
                  </Button>
                ) : null}
              </div>
            ) : (
              <div className="d-flex flex-column gap-2">
                {items.map((item, idx) => {
                  const isFindingOpen = expandedFindings.has(idx);
                  const isLastItem = idx === items.length - 1;
                  return (
                    <div
                      key={idx}
                      className="rounded border p-3"
                    >
                      <div className="d-flex gap-3 align-items-start">
                        <span
                          className="text-muted fw-semibold flex-shrink-0 pt-1"
                          style={{ fontSize: "10px", lineHeight: 1, width: "16px" }}
                        >
                          {idx + 1}
                        </span>

                        <div className="flex-grow-1 min-w-0">
                          {canManageStructure ? (
                            <Input
                              type="text"
                              bsSize="sm"
                              placeholder="What to inspect…"
                              value={item.label}
                              innerRef={
                                isLastItem ? newItemInputRef : undefined
                              }
                              className="fw-medium border-0 shadow-none bg-transparent px-0 py-0"
                              onChange={(e) =>
                                handleItemChange(idx, { label: e.target.value })
                              }
                            />
                          ) : (
                            <p className="fw-medium mb-0">
                              {item.label || (
                                <span className="text-muted fst-italic">
                                  Untitled item
                                </span>
                              )}
                            </p>
                          )}

                          {canFillFindings ? (
                            <div className="mt-2">
                              {isFindingOpen ? (
                                <>
                                  <Input
                                    type="textarea"
                                    bsSize="sm"
                                    rows={2}
                                    placeholder="Record finding…"
                                    value={item.finding ?? ""}
                                    onChange={(e) =>
                                      handleItemChange(idx, {
                                        finding: e.target.value || null,
                                      })
                                    }
                                  />
                                  {!item.finding ? (
                                    <button
                                      type="button"
                                      className="btn btn-link btn-sm p-0 text-muted mt-1"
                                      style={{ fontSize: "12px" }}
                                      onClick={() => toggleFinding(idx)}
                                    >
                                      Cancel
                                    </button>
                                  ) : null}
                                </>
                              ) : item.finding ? (
                                <p
                                  className="text-muted small mb-0 mt-1 fst-italic"
                                  role="button"
                                  onClick={() => toggleFinding(idx)}
                                  title="Click to edit"
                                >
                                  {item.finding}
                                </p>
                              ) : (
                                <button
                                  type="button"
                                  className="btn btn-link btn-sm p-0 text-muted"
                                  style={{ fontSize: "12px" }}
                                  onClick={() => toggleFinding(idx)}
                                >
                                  <i className="ri-edit-line me-1" />
                                  Add finding
                                </button>
                              )}
                            </div>
                          ) : item.finding ? (
                            <p className="text-muted small mb-0 mt-1 fst-italic">
                              {item.finding}
                            </p>
                          ) : null}
                        </div>

                        {canManageStructure ? (
                          <button
                            type="button"
                            className="btn btn-link btn-sm text-danger p-0 flex-shrink-0 align-self-start lh-1"
                            title="Remove item"
                            onClick={() => handleRemoveItem(idx)}
                          >
                            <i className="ri-delete-bin-line fs-16" />
                          </button>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

export default ChecklistTab;
